// QuickStudyStore — estado global compartilhado entre as 4 superfícies do
// tira-dúvida (card na home, view em Estudando com IA, dock no rodapé,
// pop-out window). Signals + BroadcastChannel mantém todas em sync.

import { signal } from "@preact/signals";
import {
  api,
  ApiError,
  type QuickStudyConversation,
  type QuickStudyLimits,
  type QuickStudyMessage
} from "./api";

const DEFAULT_LIMITS: QuickStudyLimits = {
  enabled: true,
  maxImagesPerMessage: 10,
  maxImageSizeMb: 5,
  maxTextChars: 8000
};

export const quickStudyLimits = signal<QuickStudyLimits>(DEFAULT_LIMITS);
export const quickStudyConversations = signal<QuickStudyConversation[]>([]);
export const quickStudyActiveId = signal<string | null>(null);
export const quickStudyMessages = signal<QuickStudyMessage[]>([]);
export const quickStudyDraftText = signal<string>("");
export const quickStudyLoading = signal<boolean>(false);
export const quickStudySending = signal<boolean>(false);
export const quickStudyError = signal<string | null>(null);
// Estado do dock persiste em localStorage pra sobreviver refresh.
const DOCK_OPEN_KEY = "quickstudy.dock.open";
function readDockOpenInitial(): boolean {
  try {
    return localStorage.getItem(DOCK_OPEN_KEY) === "1";
  } catch {
    return false;
  }
}
export const quickStudyDockOpen = signal<boolean>(readDockOpenInitial());
export function setQuickStudyDockOpen(open: boolean) {
  quickStudyDockOpen.value = open;
  try {
    if (open) localStorage.setItem(DOCK_OPEN_KEY, "1");
    else localStorage.removeItem(DOCK_OPEN_KEY);
  } catch {
    /* ignore */
  }
}

let loadedLimits = false;
let bc: BroadcastChannel | null = null;

function ensureBroadcast() {
  if (bc) return bc;
  try {
    bc = new BroadcastChannel("quickstudy-sync");
    bc.onmessage = (ev) => {
      const msg = ev.data;
      if (!msg || typeof msg !== "object") return;
      switch (msg.type) {
        case "convs-changed":
          if (Array.isArray(msg.conversations)) {
            quickStudyConversations.value = msg.conversations;
          }
          break;
        case "active-changed":
          if (typeof msg.id === "string" || msg.id === null) {
            quickStudyActiveId.value = msg.id;
            if (Array.isArray(msg.messages)) {
              quickStudyMessages.value = msg.messages;
            }
          }
          break;
        case "messages-changed":
          if (msg.conversationId === quickStudyActiveId.value && Array.isArray(msg.messages)) {
            quickStudyMessages.value = msg.messages;
          }
          break;
      }
    };
  } catch {
    bc = null;
  }
  return bc;
}

function publish(payload: Record<string, unknown>) {
  try {
    ensureBroadcast()?.postMessage(payload);
  } catch {
    /* ignore */
  }
}

export async function loadQuickStudyLimits(force = false) {
  if (loadedLimits && !force) return;
  try {
    const r = await api.getQuickStudyLimits();
    quickStudyLimits.value = r.limits;
    loadedLimits = true;
  } catch {
    // mantém defaults
  }
}

/** Modo da lista: 'active' (default) | 'archived'. Determina qual conjunto
 *  é exibido no sidebar. */
export const quickStudyListMode = signal<"active" | "archived">("active");

export async function loadQuickStudyConversations() {
  quickStudyLoading.value = true;
  quickStudyError.value = null;
  try {
    const archived = quickStudyListMode.value === "archived";
    const r = await api.listQuickStudyConversations(archived);
    quickStudyConversations.value = r.conversations;
    publish({ type: "convs-changed", conversations: r.conversations });
  } catch (e) {
    quickStudyError.value = e instanceof Error ? e.message : String(e);
  } finally {
    quickStudyLoading.value = false;
  }
}

export function setQuickStudyListMode(mode: "active" | "archived") {
  quickStudyListMode.value = mode;
  void loadQuickStudyConversations();
}

export async function archiveQuickStudyConversation(id: string, archive: boolean) {
  try {
    await api.patchQuickStudyConversation(id, { archived: archive });
    // Tira da lista atual (se acabou de arquivar quando lista=active, ou
    // restaurou quando lista=archived — ambos somem da lista corrente).
    quickStudyConversations.value = quickStudyConversations.value.filter((c) => c.id !== id);
    if (quickStudyActiveId.value === id) {
      quickStudyActiveId.value = null;
      quickStudyMessages.value = [];
    }
    publish({ type: "convs-changed", conversations: quickStudyConversations.value });
  } catch (e) {
    quickStudyError.value = e instanceof Error ? e.message : String(e);
  }
}

export async function openQuickStudyConversation(id: string) {
  quickStudyActiveId.value = id;
  quickStudyMessages.value = [];
  quickStudyLoading.value = true;
  quickStudyError.value = null;
  try {
    const r = await api.getQuickStudyMessages(id);
    quickStudyMessages.value = r.messages;
    publish({ type: "active-changed", id, messages: r.messages });
  } catch (e) {
    quickStudyError.value = e instanceof Error ? e.message : String(e);
  } finally {
    quickStudyLoading.value = false;
  }
}

export async function newQuickStudyConversation(): Promise<QuickStudyConversation | null> {
  quickStudyError.value = null;
  try {
    const r = await api.createQuickStudyConversation();
    quickStudyConversations.value = [r.conversation, ...quickStudyConversations.value];
    publish({ type: "convs-changed", conversations: quickStudyConversations.value });
    await openQuickStudyConversation(r.conversation.id);
    return r.conversation;
  } catch (e) {
    quickStudyError.value = e instanceof Error ? e.message : String(e);
    return null;
  }
}

export async function deleteQuickStudyConversation(id: string) {
  try {
    await api.deleteQuickStudyConversation(id);
    quickStudyConversations.value = quickStudyConversations.value.filter((c) => c.id !== id);
    if (quickStudyActiveId.value === id) {
      quickStudyActiveId.value = null;
      quickStudyMessages.value = [];
    }
    publish({ type: "convs-changed", conversations: quickStudyConversations.value });
  } catch (e) {
    quickStudyError.value = e instanceof Error ? e.message : String(e);
  }
}

export async function renameQuickStudyConversation(id: string, title: string) {
  try {
    const r = await api.patchQuickStudyConversation(id, { title });
    quickStudyConversations.value = quickStudyConversations.value.map((c) =>
      c.id === id ? r.conversation : c
    );
    publish({ type: "convs-changed", conversations: quickStudyConversations.value });
  } catch (e) {
    quickStudyError.value = e instanceof Error ? e.message : String(e);
  }
}

export async function sendQuickStudyMessage(
  conversationId: string,
  text: string,
  images: Array<{ mimeType: string; base64: string; sizeBytes: number }>
): Promise<{ ok: boolean; error?: string }> {
  quickStudySending.value = true;
  quickStudyError.value = null;
  try {
    const r = await api.sendQuickStudyMessage(conversationId, { text, images });
    const next = [
      ...quickStudyMessages.value,
      r.userMessage,
      ...(r.assistantMessage ? [r.assistantMessage] : [])
    ];
    quickStudyMessages.value = next;
    // Atualiza a conversa na lista (título + lastMessageAt podem ter mudado)
    quickStudyConversations.value = quickStudyConversations.value.map((c) =>
      c.id === conversationId
        ? {
            ...c,
            title: r.conversation.title ?? c.title,
            personaId: r.conversation.personaId ?? c.personaId,
            personaLabel: r.conversation.personaLabel ?? c.personaLabel,
            personaColor: r.conversation.personaColor ?? c.personaColor,
            lastMessageAt: new Date().toISOString(),
            messageCount: c.messageCount + (r.assistantMessage ? 2 : 1)
          }
        : c
    );
    publish({ type: "messages-changed", conversationId, messages: next });
    publish({ type: "convs-changed", conversations: quickStudyConversations.value });
    quickStudyDraftText.value = "";
    return { ok: true };
  } catch (e) {
    // Quando o backend devolve 402/502 com placeholder de assistant
    // (caminho novo), o helper `call` lança ApiError mas o body pode
    // conter `userMessage` + `assistantMessage`. Tenta extrair e exibir
    // pra evitar pergunta órfã na UI.
    if (e instanceof ApiError) {
      const body = e.body as
        | {
            userMessage?: QuickStudyMessage;
            assistantMessage?: QuickStudyMessage | null;
          }
        | undefined;
      if (body?.userMessage) {
        const next = [
          ...quickStudyMessages.value,
          body.userMessage,
          ...(body.assistantMessage ? [body.assistantMessage] : [])
        ];
        quickStudyMessages.value = next;
        publish({ type: "messages-changed", conversationId, messages: next });
      }
      quickStudyError.value = e.message;
      return { ok: false, error: e.message };
    }
    const msg = e instanceof Error ? e.message : String(e);
    quickStudyError.value = msg;
    return { ok: false, error: msg };
  } finally {
    quickStudySending.value = false;
  }
}

/** Apaga UMA mensagem (enviada ou recebida) da conversa. Remove localmente e
 *  no servidor (some também do histórico que vai pra IA). */
export async function deleteQuickStudyMessage(
  conversationId: string,
  messageId: string
): Promise<{ ok: boolean; error?: string }> {
  // Optimistic: tira da UI já.
  const previous = quickStudyMessages.value;
  quickStudyMessages.value = previous.filter((m) => m.id !== messageId);
  try {
    await api.deleteQuickStudyMessage(conversationId, messageId);
    publish({
      type: "messages-changed",
      conversationId,
      messages: quickStudyMessages.value
    });
    return { ok: true };
  } catch (e) {
    quickStudyMessages.value = previous; // rollback
    const msg = e instanceof Error ? e.message : String(e);
    quickStudyError.value = msg;
    return { ok: false, error: msg };
  }
}

/** Comprime uma imagem File em JPEG q=0.85, redimensiona pra max 1600px no
 *  maior lado. Devolve {mimeType, base64, sizeBytes}. */
export async function compressImageForQuickStudy(
  file: File,
  maxDim = 1600,
  quality = 0.85
): Promise<{ mimeType: string; base64: string; sizeBytes: number }> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("Falha ao decodificar imagem."));
      el.src = url;
    });
    let { width, height } = img;
    if (width > maxDim || height > maxDim) {
      if (width >= height) {
        height = Math.round((height * maxDim) / width);
        width = maxDim;
      } else {
        width = Math.round((width * maxDim) / height);
        height = maxDim;
      }
    }
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas sem contexto 2D.");
    ctx.drawImage(img, 0, 0, width, height);
    const blob: Blob = await new Promise((resolve, reject) =>
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("Falha ao gerar JPEG."))),
        "image/jpeg",
        quality
      )
    );
    const base64 = await blobToBase64(blob);
    return {
      mimeType: "image/jpeg",
      base64,
      sizeBytes: blob.size
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const r = reader.result as string;
      const idx = r.indexOf(",");
      resolve(idx >= 0 ? r.slice(idx + 1) : r);
    };
    reader.onerror = () => reject(reader.error ?? new Error("FileReader falhou."));
    reader.readAsDataURL(blob);
  });
}
